import type {
    KboSkillDefinition,
    KboSettings,
    KboToolConfiguration,
    ToolDefinition,
} from "./KboSettings";

const metadataKeys = new Set<keyof KboSettings>([
    "version",
    "activePresetName",
    "activePresetScope",
]);

function getChangedSettingPaths(
    value: unknown,
    defaults: unknown,
    path = "",
): string[] {
    if (value == null) return [];

    if (Array.isArray(value) || typeof value !== "object") {
        return path && !areSettingValuesEqual(value, defaults) ? [path] : [];
    }

    return Object.entries(value as Record<string, unknown>).flatMap(
        ([key, childValue]) =>
            getChangedSettingPaths(
                childValue,
                isSettingsObject(defaults) ? defaults[key] : undefined,
                path ? `${path}.${key}` : key,
            ),
    );
}

function areSettingValuesEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) return true;
    if (left == null || right == null) return false;

    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) return false;
        return (
            left.length === right.length &&
            left.every((value, index) =>
                areSettingValuesEqual(value, right[index]),
            )
        );
    }

    if (isSettingsObject(left) || isSettingsObject(right)) {
        if (!isSettingsObject(left) || !isSettingsObject(right)) return false;
        const keys = new Set([
            ...Object.keys(left),
            ...Object.keys(right),
        ]);
        return Array.from(keys).every((key) =>
            areSettingValuesEqual(left[key], right[key]),
        );
    }

    return false;
}

export function hasKboSettingsOverrides(
    settings: KboSettings | null | undefined,
    defaults?: KboSettings | null,
): settings is KboSettings {
    if (!settings) return false;

    return getChangedSettingPaths(settings, defaults).some(
        (path) => !metadataKeys.has(path as keyof KboSettings),
    );
}

export function getKboSettingsOverrideSummary(
    settings: KboSettings | null | undefined,
    defaults?: KboSettings | null,
): string[] {
    if (!settings) return [];

    const configuredPaths = new Set(
        getChangedSettingPaths(settings, defaults).filter(
            (path) => !metadataKeys.has(path as keyof KboSettings),
        ),
    );
    const mainAgentModelName = settings.llm?.mainAgent?.modelName;
    const powerPointAgentModelName = settings.llm?.powerPointAgent?.modelName;
    const reducerModelName = settings.llm?.reducer?.modelName;
    const mainAgentTemperature = settings.llm?.mainAgent?.temperature;
    const mainAgentThinkingEffortLevel =
        settings.llm?.mainAgent?.thinkingEffortLevel;
    const analysisPromptPaths = Array.from(configuredPaths).filter(
        (path) => path.startsWith("analysisPrompts."),
    );
    const analysisPromptCount = analysisPromptPaths.filter(
        (path) => !path.endsWith("Temperature"),
    ).length;
    const sectionLabels: Record<string, string> = {
        llm: "LLM settings modified",
        embedding: "Embedding settings modified",
        toolConfigurations: "Tool configuration modified",
        systemPrompt: "System prompt settings modified",
        prompts: "Prompt settings modified",
        chatHistory: "Chat history settings modified",
        memory: "Memory settings modified",
        search: "Search settings modified",
        analysis: "Analysis settings modified",
        query: "Query settings modified",
        messages: "Message settings modified",
        experimental: "Experimental settings modified",
    };
    const labels = Array.from(configuredPaths)
        .filter(
            (path) =>
                !path.startsWith("analysisPrompts."),
        )
        .map((path): string | null => {
            switch (path) {
                case "inheritFromTenantDefaults":
                    return settings.inheritFromTenantDefaults
                        ? "Tenant defaults"
                        : null;
                case "prompts.systemPromptTemplate":
                    return "System prompt modified";
                case "systemPrompt.organizationContext":
                    return "Organization context modified";
                case "prompts.authenticxBackground":
                    return "Authenticx background modified";
                case "prompts.chatNameGenerationPrompt":
                    return "Chat name prompt modified";
                case "prompts.chatNameGenerationTemperature":
                    return "Chat name temperature modified";
                case "llm.mainAgent.modelName":
                    return mainAgentModelName
                        ? `Model: ${mainAgentModelName}`
                        : "Model modified";
                case "llm.powerPointAgent.modelName":
                    return powerPointAgentModelName
                        ? `PowerPoint: ${powerPointAgentModelName}`
                        : "PowerPoint model modified";
                case "llm.reducer.modelName":
                    return reducerModelName
                        ? `Reducer: ${reducerModelName}`
                        : "Reducer model modified";
                case "llm.mainAgent.temperature":
                    return mainAgentTemperature != null
                        ? `Temp: ${mainAgentTemperature}`
                        : "Temperature modified";
                case "llm.mainAgent.thinkingEffortLevel":
                    return mainAgentThinkingEffortLevel
                        ? `Thinking: ${mainAgentThinkingEffortLevel}`
                        : "Thinking effort modified";
                case "selectedSkillNames": {
                    const selectedSkillCount =
                        settings.selectedSkillNames?.length ?? 0;
                    return selectedSkillCount > 0
                        ? `${selectedSkillCount} skill(s)`
                        : "Skills cleared";
                }
                default: {
                    const section = path.split(".")[0];
                    return (
                        sectionLabels[section] ??
                        `${section
                            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
                            .replace(/^./, (character) =>
                                character.toUpperCase(),
                            )} modified`
                    );
                }
            }
        })
        .filter((label): label is string => label != null);

    if (analysisPromptPaths.length > 0) {
        labels.push(
            analysisPromptCount > 0
                ? `${analysisPromptCount} analysis prompt override(s)`
                : "Analysis prompt settings modified",
        );
    }

    if (settings.inheritFromTenantDefaults) {
        return [
            "Tenant defaults",
            ...Array.from(new Set(labels)).filter(
                (label) => label !== "Tenant defaults",
            ),
        ];
    }

    return Array.from(new Set(labels));
}

export function cloneKboSettings(settings: KboSettings): KboSettings {
    return JSON.parse(JSON.stringify(settings)) as KboSettings;
}

export function cloneNullableKboSettings(
    settings: KboSettings | null | undefined,
): KboSettings | null {
    return settings ? cloneKboSettings(settings) : null;
}

function isSettingsObject(value: unknown): value is Record<string, unknown> {
    return (
        value != null &&
        typeof value === "object" &&
        !Array.isArray(value)
    );
}

function removeInheritedValues(value: unknown, defaults: unknown): unknown {
    if (value == null || areSettingValuesEqual(value, defaults)) {
        return undefined;
    }

    if (Array.isArray(value) || !isSettingsObject(value)) {
        return value;
    }

    const overrides = Object.fromEntries(
        Object.entries(value)
            .map(([key, childValue]) => [
                key,
                removeInheritedValues(
                    childValue,
                    isSettingsObject(defaults) ? defaults[key] : undefined,
                ),
            ])
            .filter(([, childValue]) => childValue !== undefined),
    );

    return Object.keys(overrides).length > 0 ? overrides : undefined;
}

export function toSparseKboSettings(
    settings: KboSettings,
    defaults: KboSettings | null | undefined,
): KboSettings {
    return (
        (removeInheritedValues(settings, defaults) as
            | KboSettings
            | undefined) ?? {}
    );
}

function containsPresetReset(patch: unknown, preset: unknown): boolean {
    if (patch === null) {
        return preset != null;
    }

    if (!isSettingsObject(patch)) {
        return false;
    }

    return Object.entries(patch).some(([key, value]) =>
        containsPresetReset(
            value,
            isSettingsObject(preset) ? preset[key] : undefined,
        ),
    );
}

/**
 * A nullish field in sparse settings means "inherit", so it cannot also
 * override a non-null preset value. Detect that shape generically and detach
 * the named preset before the settings are persisted against normal defaults.
 */
export function hasKboPresetFieldReset(
    patch: Partial<KboSettings>,
    preset: KboSettings,
): boolean {
    return containsPresetReset(patch, preset);
}

function hydrateInheritedValue(base: unknown, override: unknown): unknown {
    if (override == null) {
        return base;
    }

    if (Array.isArray(override)) {
        return override.map((value) =>
            isSettingsObject(value)
                ? hydrateInheritedValue({}, value)
                : value,
        );
    }

    if (!isSettingsObject(override)) {
        return override;
    }

    const hydrated: Record<string, unknown> = isSettingsObject(base)
        ? { ...base }
        : {};

    for (const [key, value] of Object.entries(override)) {
        hydrated[key] = hydrateInheritedValue(hydrated[key], value);
    }

    return hydrated;
}

/**
 * Rebuilds the effective settings for a persisted session that references a
 * preset. Persisted session values are sparse overrides: nullish values mean
 * "inherit". With an active preset, that inheritance must start at the preset
 * rather than skipping directly to the user's resolved defaults.
 */
export function hydrateKboPresetSettings(
    preset: KboSettings,
    sessionOverrides: KboSettings,
): KboSettings {
    const hydrated = hydrateInheritedValue(
        cloneKboSettings(preset),
        sessionOverrides,
    ) as KboSettings;

    // Session tool settings can contain only the changed functions. Preserve
    // the remainder of the preset while applying those keyed overrides.
    const toolConfigurations = mergeToolConfigurations(
        preset.toolConfigurations,
        sessionOverrides.toolConfigurations,
    );
    if (toolConfigurations.length > 0) {
        hydrated.toolConfigurations = toolConfigurations;
    }

    return hydrated;
}

export function cloneToolConfigurations(
    toolConfigurations: KboToolConfiguration[] | null | undefined,
): KboToolConfiguration[] {
    return (
        toolConfigurations?.map((config) => ({
            ...config,
            excludedProperties: config.excludedProperties
                ? [...config.excludedProperties]
                : config.excludedProperties,
        })) ?? []
    );
}

export function mergeToolConfigurations(
    baseToolConfigurations: KboToolConfiguration[] | null | undefined,
    overrideToolConfigurations: KboToolConfiguration[] | null | undefined,
): KboToolConfiguration[] {
    const mergedToolConfigurations = cloneToolConfigurations(
        baseToolConfigurations,
    );
    const configurationsByName = new Map(
        mergedToolConfigurations.map((config) => [
            config.functionName.toLocaleLowerCase(),
            config,
        ]),
    );

    for (const overrideConfig of cloneToolConfigurations(
        overrideToolConfigurations,
    )) {
        const lookupName = overrideConfig.functionName.toLocaleLowerCase();
        const existingConfig = configurationsByName.get(lookupName);

        if (existingConfig) {
            Object.assign(existingConfig, overrideConfig);
        } else {
            mergedToolConfigurations.push(overrideConfig);
            configurationsByName.set(lookupName, overrideConfig);
        }
    }

    return mergedToolConfigurations;
}

export function getSkillRequiredToolNames(
    availableSkills: KboSkillDefinition[],
    selectedSkillNames: string[] | null | undefined,
): Record<string, string[]> {
    const selectedSkillSet = new Set(
        (selectedSkillNames ?? []).map((name) => name.toLocaleLowerCase()),
    );
    const requiredTools: Record<string, string[]> = {};

    for (const skill of availableSkills) {
        if (!selectedSkillSet.has(skill.name.toLocaleLowerCase())) {
            continue;
        }

        for (const toolName of skill.toolNames ?? []) {
            const trimmedToolName = toolName.trim();
            if (!trimmedToolName) continue;

            requiredTools[trimmedToolName] ??= [];
            requiredTools[trimmedToolName].push(skill.name);
        }
    }

    return requiredTools;
}

export function getSkillScopedToolConfigurations(
    baseToolConfigurations: KboToolConfiguration[] | null | undefined,
    availableTools: ToolDefinition[],
    availableSkills: KboSkillDefinition[],
    selectedSkillNames: string[] | null | undefined,
): KboToolConfiguration[] {
    const toolConfigurations = cloneToolConfigurations(baseToolConfigurations);
    const requiredToolNames = getSkillRequiredToolNames(
        availableSkills,
        selectedSkillNames,
    );
    const requiredToolNameSet = new Set(
        Object.keys(requiredToolNames).map((name) => name.toLocaleLowerCase()),
    );

    if (requiredToolNameSet.size === 0) {
        return toolConfigurations;
    }

    const baseConfigurationsByName = new Map(
        toolConfigurations.map((config) => [
            config.functionName.toLocaleLowerCase(),
            config,
        ]),
    );
    const availableToolsByName = new Map(
        availableTools.map((tool) => [
            tool.functionName.toLocaleLowerCase(),
            tool,
        ]),
    );
    const orderedToolNames = [
        ...availableTools
            .filter((tool) =>
                requiredToolNameSet.has(tool.functionName.toLocaleLowerCase()),
            )
            .map((tool) => tool.functionName),
        ...Object.keys(requiredToolNames).filter(
            (toolName) =>
                !availableToolsByName.has(toolName.toLocaleLowerCase()),
        ),
    ];

    for (const toolName of orderedToolNames) {
        const lookupName = toolName.toLocaleLowerCase();
        const existing = baseConfigurationsByName.get(lookupName);
        const availableTool = availableToolsByName.get(lookupName);
        const updatedConfig = {
            functionName:
                existing?.functionName ?? availableTool?.functionName ?? toolName,
            enabled: true,
            groupName: existing?.groupName,
            namespace: existing?.namespace ?? availableTool?.namespace,
            loadingMode:
                existing?.loadingMode ?? availableTool?.defaultLoadingMode,
            excludedProperties: existing?.excludedProperties,
        };

        if (existing) {
            Object.assign(existing, updatedConfig);
        } else {
            toolConfigurations.push(updatedConfig);
            baseConfigurationsByName.set(lookupName, updatedConfig);
        }
    }

    return toolConfigurations;
}
